<template lang="html">

  <section 
    :class="['grid wordsearch', {'completed': completed }]"
  >
    <transition name="fade">
      <div 
        class="blank"
        v-if="!grid.loaded"
      >
        <Spinner type="cube" />
      </div>
    </transition>

    <transition name="fade">
      
      <div 
        :class="['grid-wrapper',{'loaded': grid.loaded}]"
        v-if="grid.loaded"
        ref="grid"            
        v-touch:start="onMousedown" 
        v-touch:moving="tOnMousemove"
      >
        <svg 
          v-if="(isPlaying || completed) && grid.loaded"
          :viewBox="`0 0 ${ grid.rows } ${ grid.cols }`" 
          xmlns="http://www.w3.org/2000/svg" 
          class="lines"          
        >
          <line 
            v-for="word in words" 
            :key="`line-${word.word}`" 
            v-if="word.found" 
            :x1="word.startCell.col + 0.5" 
            :y1="word.startCell.row + 0.5" 
            :x2="word.endCell.col + 0.5" 
            :y2="word.endCell.row + 0.5" 
            :class="[{'answer': word.found}, `dir-${ word.direction }`]"
          />
          
          <line 
            v-if="startCell" 
            :x1="startCell.col + 0.5" 
            :y1="startCell.row + 0.5" 
            :x2="xPos" 
            :y2="yPos" 
          />
        </svg>

        <div
          class="cell-row"
          v-for="(row, i) in grid.rows"
          :key="`col-${i}`"
        >
          <Cell
            v-for="(col, j) in grid.cols"
            :key="`row-${j}`"
            :row="i"
            :col="j"
            :cell="getCellAtPosition(i,j)"
            :ref="`c-${i}-${j}`"
          />
        </div>
        
      </div>
    </transition>

  </section>

</template>

<script lang="js">

  import { mapActions, mapState, mapGetters } from 'vuex'
  import Cell from './Cell'
  import Spinner from '@puzzleapps/shared/src/components/ui/Spinner'
  import { throttle } from 'throttle-debounce'

  export default  {
    name: 'grid',
    props: [
      'type',
    ],
    components: {
      Cell,
      Spinner,
    },

    data() {
      return {
        xPos: 0,
        yPos: 0,
      }
    },

    mounted() {
      this.gameInit(this.type)

      // TODO use a global event
      this.onMouseup = this.onMouseup.bind(this)
      this.onMousemove = this.onMousemove.bind(this)
      window.addEventListener('mouseup', this.onMouseup)
      window.addEventListener('touchend', this.onMouseup, {passive:false})
    },

    destroyed() {
      this.clearPuzzleState()
      window.removeEventListener('mouseup', this.onMouseup)
      window.removeEventListener('touchend', this.onMouseup, {passive:false})
    },

    methods: {
      ...mapActions({
        gameInit: 'game/init',
        setPlayId: 'game/setPlayId',
        clearPuzzleState: 'game/clearPuzzleState',
        setInputAtPos: 'grid/setInputAtPos',
        selectCell: 'grid/selectCell',
        undoRedo: 'undoRedo/undoRedo',
        toggleIsAcross: 'grid/toggleIsAcross',

        setWords: 'wordlist/setWords',

        startDrag: 'mouseDragger/startDrag',
        dragOver: 'mouseDragger/dragOver',
        endDrag: 'mouseDragger/endDrag',
      }),

      findUpTag(el, tag) {
        while (el.parentNode) {
          el = el.parentNode
          if (el.tagName === tag) return el            
        }
        return null
      },

      onMousedown(e) {
        let p = (e.touches) ? e.touches[0] : e
        let el = this.findUpTag(e.target, 'DIV')        
        if (!el) return

        let cmp = this.$children.find(c => c.$el === el)
        this.updateMouseXY(p.clientX, p.clientY)
        
        if (cmp)
          this.startDrag(cmp.cell)
      },

      onMousemove(e) {
        if (!this.isDragging) return   

        let p = (e.touches) ? e.touches[0] : e
        let el = document.elementFromPoint(p.clientX, p.clientY)
        el = this.findUpTag(el, 'DIV')
        if (!el) return

        let cmp = this.$children.find(c => c.$el === el)
        
        this.updateMouseXY(p.clientX, p.clientY)

        if (cmp)
          this.dragOver(cmp.cell)
      },

      onMouseup() {        
        this.endDrag()
      },

      // arrow syntax causes throttle to fail :(
      tOnMousemove: throttle(15, function(e) {
        this.onMousemove(e)
      }),

      updateMouseXY(clientX, clientY) {
        let rect = this.$refs.grid.getBoundingClientRect()

        this.xPos = ((clientX - rect.left) / rect.width * this.grid.cols)
        this.yPos = ((clientY - rect.top) / rect.height * this.grid.rows)
      },

    },
    
    computed: {
      ...mapState({
        grid: state => state.grid,
        isPencil: state => state.grid.isPencil,
        isPlaying: state => state.game.isPlaying,
        completed: state => state.game.completed,
        puzzleId: state => state.game.puzzleId,
        gameType: state => state.game.type,

        startCell: state => state.mouseDragger.startCell,
        isDragging: state => state.mouseDragger.isDragging,
        endCell: state => state.mouseDragger.endCell,
        words: state => state.wordlist.words,
      }),

      ...mapGetters({
        getCellAtPosition: 'grid/getCellAtPosition',
      }),
      
    }
}


</script>

<style scoped lang="scss">

  @import '@puzzleapps/shared/src/style/variables';

  @keyframes splash {
    from {
      opacity: 0;
      stroke-width: 0.8px;
    }
    50% {
      opacity: 1;
      stroke-width: 1px;
    }
    to {
      stroke-width: 0.8px;
    }
  }

  .lines {
    position: absolute;
    left: 0;
    top: 0;
    pointer-events: none;

    line {
      opacity: 0.7;
      stroke: var(--wordsearch-drag-color);
      stroke-width: 0.8px;
      stroke-linecap: round;
      transition: all 0.5s;

      &.answer {
        animation: splash 0.5s normal forwards ease-in-out;
        // animation-iteration-count: 1;
        // transform: scale(1.1);

        &.dir-0 {
          stroke: var(--wordsearch-selected-color); //#00D8AE
        }
        &.dir-1 {
          stroke: var(--wordsearch-selected-color);//#27ae60;
        }
        &.dir-2 {
          stroke: var(--wordsearch-selected-color);//#0BE1F7;
        }
        &.dir-3 {
          stroke: var(--wordsearch-selected-color);//#DD8AFF;
        }
        &.dir-4 {
          stroke: var(--wordsearch-selected-color);//#7CBDFF;
        }
        &.dir-5 {
          stroke: var(--wordsearch-selected-color);//#FFEE3B;
        }
        &.dir-6 {
          stroke: var(--wordsearch-selected-color);//#FF8119;
        }
        &.dir-7 {
          stroke: var(--wordsearch-selected-color);//#FF68BD;
        }
      }
    }
  }

  .grid-wrapper.fade-leave-active,
  .fade-enter-active {
    transition: all 0.5s cubic-bezier(1.0, 0.5, 0.8, 1.0)!important;
  }
  
  .grid-wrapper.fade-leave-to,
  .fade-enter, 
  .slide-fade-leave-to {
    opacity: 0;
  }

  .grid {
    width: 100%;
    box-sizing: border-box;
    position: relative;

    &:before {
      content: "";
      display: block;
      padding-bottom: 100%;
    }

    .blank {
      display: flex;
      justify-content: center;
      align-items: center;
      background: #f5f5f5;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: -1;
      border: 2px solid white;


      .spinner {
        transition: opacity 0.5s;
      }

      &.fade-leave-active,
      &.fade-leave-to {
        position: absolute;

        .spinner {
          opacity: 0;
        }
      }
    }

    .grid-wrapper {
      position: absolute;
      width: 100%;
      box-sizing: border-box;
      background: #fff;
      top: 0;
      left: 0;
      overscroll-behavior-y: contain;
      touch-action: none;
    }

    .cell-row {
      display: flex;
      width: 100%;
    }

  }
</style>

